import {
    TokenAmount,
    TransactionDetailRow,
    TransactionFeeSection,
    TransactionDialogContent,
    TransactionSection,
} from "@/components";
import { useChangeDurationTransaction, useTokenApprovalV2 } from "@/hooks";
import { type TokenId, TOKEN_IDS } from "@/types";
import { Divider } from "@/components";
import { useCallback } from "react";
import { getGatewayContractAddress, getLockAddress } from "@/config";
import { mainnet } from "viem/chains";
import { useAccount } from "wagmi";

export interface ChangeDurationTransactionProps {
    lockAmount: number;
    existingDuration: number;
    newDuration: number;
    lockTokenId: TokenId;
    onClose?: () => void;
}

export function ChangeDurationTransaction({
    lockAmount,
    existingDuration,
    newDuration,
    onClose,
}: ChangeDurationTransactionProps) {
    const { address } = useAccount();

    const {
        isApproved,
        isLoading: isTokenApprovalLoading,
        isApproving,
        isCheckingAllowance,
        withApproval,
    } = useTokenApprovalV2({
        tokenId: TOKEN_IDS.LIUSD,
        tokenAddress: getLockAddress(mainnet.id, existingDuration),
        amount: lockAmount,
        spender: getGatewayContractAddress(mainnet.id),
    });

    const { gasPriceUSD, apy, executeChangeDurationTransaction, isLoading, isExecuting } = useChangeDurationTransaction(
        {
            lockAmount: BigInt(0), // NOTE: lockAmount is calculated inside the hook based on existingDuration
            existingDuration,
            newDuration,
            hasApproval: isApproved,
        },
    );

    const handleConfirm = useCallback(async () => {
        if (!address) return console.error("Wallet not connected");

        try {
            const hasExecuted = await withApproval(
                async () => {
                    await executeChangeDurationTransaction();
                    return true;
                },
                { isExecuting, amount: lockAmount },
            );

            if (!hasExecuted) return;

            onClose?.();
        } catch (error) {
            console.error("Error confirming change duration transaction:", error);
        }
    }, [withApproval, executeChangeDurationTransaction, onClose, address]);

    const LOCK_TEXT = <TokenAmount amount={lockAmount} tokenId={TOKEN_IDS.IUSD} size="md" />;
    const DURATION_TEXT = `${newDuration} week`;
    const APY_TEXT = `${apy}% APY`;
    // const PROJECTED_EARNINGS_TEXT = `${Number.parseFloat(projectedEarnings).toFixed(2)} iUSD`; NOTE: not working
    const CONFIRM_TEXT = "Confirm Lock";
    const APPROVE_AND_CONFIRM_TEXT = isApproved ? CONFIRM_TEXT : "Approve & Confirm Lock";

    return (
        <TransactionDialogContent
            onClose={onClose}
            onConfirm={handleConfirm}
            isLoading={isLoading || isTokenApprovalLoading || isExecuting || isApproving || isCheckingAllowance}
            confirmText={APPROVE_AND_CONFIRM_TEXT}
            className="min-w-sm w-[420px]"
        >
            <TransactionSection>
                <TransactionDetailRow label="Lock" value={LOCK_TEXT} />
                <TransactionDetailRow label="Duration" value={DURATION_TEXT} />
                <TransactionDetailRow
                    label="Rate"
                    slotProps={{
                        value: { className: "text-secondary" },
                    }}
                    value={
                        <span className="inline-flex">
                            {APY_TEXT}
                            <sup className="mt-4">*</sup>
                        </span>
                    }
                />
                {/* <TransactionDetailRow label="Projected earnings" value={PROJECTED_EARNINGS_TEXT} /> */}
            </TransactionSection>
            <Divider />
            <TransactionFeeSection gasPriceUSD={gasPriceUSD} />
        </TransactionDialogContent>
    );
}
